// Create / edit form for a branch, shown in a modal. Used by BranchList.
//
// A branch must have a parent hub (chosen from a dropdown). Branch codes must be
// unique within their parent hub — checked instantly client-side and enforced by
// the database unique index (we catch that error too).

import { useState } from "react";
import { Button, Field, Input, Modal, Select } from "../../components/ui";
import type { Branch, BranchInput, Hub } from "../../lib/types";
import { isUniqueViolation, getErrorMessage } from "../../lib/errors";
import { parseOptionalNumber, isValidLatitude, isValidLongitude } from "../../lib/validation";
import { createBranch, updateBranch } from "./branchesApi";

interface Props {
  accountId: string;
  branch: Branch | null; // null = creating
  hubs: Hub[]; // for the parent-hub dropdown
  existingBranches: Branch[]; // for the duplicate-code check
  onClose: () => void;
  onSaved: () => void;
}

export default function BranchForm({
  accountId,
  branch,
  hubs,
  existingBranches,
  onClose,
  onSaved,
}: Props) {
  const [hubId, setHubId] = useState(branch?.hubId ?? hubs[0]?.id ?? "");
  const [name, setName] = useState(branch?.name ?? "");
  const [code, setCode] = useState(branch?.code ?? "");
  const [city, setCity] = useState(branch?.city ?? "");
  const [state, setState] = useState(branch?.state ?? "");
  const [capacity, setCapacity] = useState(branch?.capacity != null ? String(branch.capacity) : "");
  const [sharePct, setSharePct] = useState(branch?.sharePct != null ? String(branch.sharePct) : "");
  const [manager, setManager] = useState(branch?.manager ?? "");
  const [lat, setLat] = useState(branch?.lat != null ? String(branch.lat) : "");
  const [lng, setLng] = useState(branch?.lng != null ? String(branch.lng) : "");
  const [active, setActive] = useState(branch?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Is this code already used by a DIFFERENT branch under the same hub? */
  function codeIsDuplicate(value: string): boolean {
    const c = value.trim().toLowerCase();
    if (!c) return false;
    return existingBranches.some(
      (b) => b.id !== branch?.id && b.hubId === hubId && (b.code ?? "").trim().toLowerCase() === c
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!hubId) {
      setError("Please choose a parent hub.");
      return;
    }
    if (!name.trim()) {
      setError("Branch name is required.");
      return;
    }
    if (codeIsDuplicate(code)) {
      setError(`Code "${code.trim()}" is already used by another branch in this hub.`);
      return;
    }

    // Validate + normalize numeric fields before they can reach Supabase.
    const latP = parseOptionalNumber(lat);
    const lngP = parseOptionalNumber(lng);
    const capP = parseOptionalNumber(capacity);
    if (!latP.ok || (latP.value !== null && !isValidLatitude(latP.value))) {
      setError("Latitude must be a number between -90 and 90.");
      return;
    }
    if (!lngP.ok || (lngP.value !== null && !isValidLongitude(lngP.value))) {
      setError("Longitude must be a number between -180 and 180.");
      return;
    }
    if (!capP.ok || (capP.value !== null && capP.value < 0)) {
      setError("Capacity must be a positive number.");
      return;
    }
    const shareP = parseOptionalNumber(sharePct);
    if (!shareP.ok || (shareP.value !== null && (shareP.value < 0 || shareP.value > 100))) {
      setError("Share % must be a number between 0 and 100.");
      return;
    }

    setBusy(true);
    setError(null);
    const input: BranchInput = {
      hubId,
      name,
      code,
      city,
      state,
      lat: latP.value,
      lng: lngP.value,
      capacity: capP.value === null ? null : Math.round(capP.value),
      sharePct: shareP.value,
      manager,
      active,
    };
    try {
      if (branch) {
        await updateBranch(accountId, branch.id, input);
      } else {
        await createBranch(accountId, input);
      }
      onSaved();
    } catch (err: unknown) {
      if (isUniqueViolation(err)) {
        setError(`Code "${code.trim()}" is already used by another branch in this hub.`);
      } else {
        setError(getErrorMessage(err, "Failed to save branch."));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={branch ? "Edit branch" : "New branch"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Parent hub *">
          <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
            {hubs.length === 0 && <option value="">No hubs yet — create a hub first</option>}
            {hubs.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
                {[h.city, h.state].filter(Boolean).length
                  ? ` (${[h.city, h.state].filter(Boolean).join(", ")})`
                  : ""}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Branch name *">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tampa Branch" autoFocus />
          </Field>
          <Field label="Branch code">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. TPA" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="State">
            <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. FL" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input
              type="number"
              step="any"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="27.9506"
            />
          </Field>
          <Field label="Longitude">
            <Input
              type="number"
              step="any"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="-82.4572"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Capacity (pkg/day)">
            <Input
              type="number"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="75000"
              min={0}
            />
          </Field>
          <Field label="Branch manager">
            <Input value={manager} onChange={(e) => setManager(e.target.value)} />
          </Field>
        </div>

        <Field label="Share % of hub (optional)">
          <Input
            type="number"
            value={sharePct}
            onChange={(e) => setSharePct(e.target.value)}
            placeholder="e.g. 40 — leave blank for equal split / actual-driven"
            min={0}
            max={100}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || hubs.length === 0}>
            {busy ? "Saving…" : branch ? "Save changes" : "Create branch"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
